import {
  CustomExpenseDeductedTypeEnum as GQLDeducted,
  CustomExpenseDueEnum as GQLDue,
  type SettlementCustomExpenseInput as GQLInput,
  type SettlementCustomExpense as GQLItem,
} from '../../../generated/api.graphql';
import { take } from '../../../util';

/** Who a custom expense is due to. See `SettlementType.ts` for why these are literals. */
export const CustomExpenseDueEnum = Object.freeze({
  ToArtist: GQLDue.ToArtist,
  ToVenue: GQLDue.ToVenue,
});

export type CustomExpenseDueEnum = (typeof CustomExpenseDueEnum)[keyof typeof CustomExpenseDueEnum];

/** Whether a custom expense comes off gross or net. */
export const CustomExpenseDeductedTypeEnum = Object.freeze({
  Gross: GQLDeducted.Gross,
  Net: GQLDeducted.Net,
});

export type CustomExpenseDeductedTypeEnum = (typeof CustomExpenseDeductedTypeEnum)[keyof typeof CustomExpenseDeductedTypeEnum];

/** A custom expense line as the server sends it. */
export type CustomExpense = {
  id?: number;
  amount?: number;
  description?: string;
  due?: CustomExpenseDueEnum;
  deductedType?: CustomExpenseDeductedTypeEnum;
};

/**
 * A custom expense line on the way back. `_destroy` marks a row for removal, so it belongs only
 * here -- the read never selects it. Spelled out for the reason `Deal.ts` gives.
 */
export type CustomExpenseInput = {
  id?: number;
  amount?: number;
  description?: string;
  due?: CustomExpenseDueEnum;
  deductedType?: CustomExpenseDeductedTypeEnum;
  _destroy?: boolean;
};

export const fromGql = (item: GQLItem): CustomExpense => ({
  id: item.id,
  amount: item.amount ?? undefined,
  description: item.description ?? undefined,
  due: item.due ?? undefined,
  deductedType: item.deductedType ?? undefined,
});

const GQL_FIELDS = ['id', '_destroy', 'amount', 'description', 'due', 'deductedType'] as const;

export const toGqlInput = (expense: CustomExpenseInput): GQLInput =>
  take<GQLInput, CustomExpenseInput>(expense, ...GQL_FIELDS);
